// CONTRACT:P1-CANONICAL-ENVELOPE.1.0
/** usgsSource — the LIVE USGS earthquake source: poll the USGS GeoJSON feed, map each
 *  earthquake feature to a clear-shell `alert` envelope. The pure data layer behind
 *  usgsCapability (the exact split as adsbSource behind adsbCapability).
 *
 *  PURITY NOTE (the SDK-purity exit gate, mirrors adsbSource/multiIntDataset): this module
 *  imports ONLY the public-SDK envelope types (../../../../packages/contracts-ts) — NO host
 *  internal, NO abac-pdp, NO pdp.ts. usgsCapability imports this file; the SDK-purity scanner
 *  whitelists it as "its own dataset".
 *
 *  SOURCE: USGS Earthquake Hazards Program GeoJSON summary feed (all_hour) — NO API KEY,
 *  CORS-permissive, US-gov public-domain data. Each GeoJSON Feature is a single earthquake:
 *  properties.{mag, place, time(ms), title, url, …} + geometry Point [lon, lat, depthKm].
 *  We poll on a conservative ≥1min interval (the feed updates ~every minute) and NEVER hammer;
 *  a failed/throttled poll falls back to the small bundled snapshot rather than retrying tightly.
 *
 *  CLEAR-SHELL ONLY: public earthquake data is UNCLASSIFIED — every envelope is
 *  payload_mode:"clear", leakage_profile:"clear", with NO sealed/encrypted inner. Affiliation
 *  is "neutral" (a natural event — never hostile, never friendly). REL is EMPTY (UNCLASSIFIED
 *  with empty REL = visible to all — matches the adsbSource UNCLASS default). */

import type { CanonicalEnvelope } from "../../../../packages/contracts-ts/src/index.ts";

/** USGS Earthquake GeoJSON summary feed — all earthquakes in the past hour. Anonymous,
 *  CORS-permissive, public domain. */
export const USGS_URL =
  "https://earthquake.usgs.gov/earthquakes/feed/v1.0/summary/all_hour.geojson";

/** Conservative poll interval (ms). The summary feed regenerates ~every minute; ≥1min keeps
 *  us in step without hammering. The instruction asks for ~5min — the default. Never faster
 *  than MIN_POLL_MS. */
export const DEFAULT_POLL_MS = 300_000;
export const MIN_POLL_MS = 60_000;

/** The envelope kind USGS publishes (alerts). Exported so capability + check agree. */
export const USGS_KIND = "alert" as const;

/** A single USGS GeoJSON earthquake feature (the subset we read). */
export interface UsgsFeature {
  type: "Feature";
  id?: string;
  properties: {
    mag?: number | null;
    place?: string | null;
    time?: number | null; // epoch MILLISECONDS
    updated?: number | null;
    url?: string | null;
    title?: string | null;
    [key: string]: unknown;
  } | null;
  /** Point geometry [lon, lat, depthKm]. depth is positive km BELOW the surface. */
  geometry: { type: "Point"; coordinates: number[] } | null;
}

/** The USGS GeoJSON FeatureCollection response shape. */
export interface UsgsResponse {
  type: "FeatureCollection";
  features: UsgsFeature[] | null;
  metadata?: { generated?: number; title?: string };
}

/** A tiny BUNDLED snapshot (two real-shaped quakes) for the offline/dev/test fallback path —
 *  embedded inline (no extra JSON file) so the source stays single-file like the live feed. */
export const USGS_SNAPSHOT_RESPONSE: UsgsResponse = {
  type: "FeatureCollection",
  metadata: { generated: 1_750_000_000_000, title: "USGS Magnitude All — bundled snapshot" },
  features: [
    {
      type: "Feature",
      id: "snapshot-ci-1",
      properties: {
        mag: 4.2,
        place: "112 km SSW of Adak, Alaska",
        time: 1_749_999_900_000,
        updated: 1_749_999_950_000,
        url: "https://earthquake.usgs.gov/earthquakes/eventpage/snapshot-ci-1",
        title: "M 4.2 - 112 km SSW of Adak, Alaska",
      },
      geometry: { type: "Point", coordinates: [-176.9, 50.6, 35.4] },
    },
    {
      type: "Feature",
      id: "snapshot-ci-2",
      properties: {
        mag: 1.8,
        place: "10 km NE of Ridgecrest, CA",
        time: 1_749_999_800_000,
        updated: 1_749_999_820_000,
        url: "https://earthquake.usgs.gov/earthquakes/eventpage/snapshot-ci-2",
        title: "M 1.8 - 10 km NE of Ridgecrest, CA",
      },
      geometry: { type: "Point", coordinates: [-117.6, 35.7, 6.1] },
    },
  ],
};

/** A stable ISO timestamp from a USGS epoch-MILLISECONDS field. */
function isoFromMs(epochMs: number | null | undefined, fallbackMs: number): string {
  const ms = typeof epochMs === "number" && Number.isFinite(epochMs) ? epochMs : fallbackMs;
  return new Date(ms).toISOString();
}

/** Is a USGS feature mappable to an alert? It must have Point geometry with finite lon AND
 *  lat. A feature with absent/malformed coordinates is DROPPED (fail-closed) rather than
 *  placed at (0,0) — never silently normalize a malformed position. */
export function isMappable(feature: UsgsFeature): boolean {
  const c = feature.geometry?.coordinates;
  return (
    feature.geometry?.type === "Point" &&
    Array.isArray(c) &&
    typeof c[0] === "number" &&
    Number.isFinite(c[0]) &&
    typeof c[1] === "number" &&
    Number.isFinite(c[1])
  );
}

/** Map a single USGS earthquake feature → a clear-shell `alert` envelope. The magnitude,
 *  depth (km), and place ride in `properties` so the host can paint + inspect. Deterministic
 *  for a fixed (feature, snapshotMs) so re-publishing the same quake updates (not dupes). */
export function featureToEnvelope(feature: UsgsFeature, snapshotMs: number): CanonicalEnvelope {
  const props = feature.properties ?? {};
  const coords = feature.geometry?.coordinates ?? [];
  const lon = coords[0] as number;
  const lat = coords[1] as number;
  // isMappable already proved [lon,lat] are finite; belt-and-suspenders so a precondition
  // violation fails CLOSED + loud instead of emitting a NaN/[0,0]-coordinate phantom quake.
  if (!Number.isFinite(lon) || !Number.isFinite(lat)) {
    throw new Error(`usgsSource.featureToEnvelope: non-finite coords reached the mapper (id=${String(feature.id ?? "?")})`);
  }
  const depthKm = typeof coords[2] === "number" ? coords[2] : null;
  const mag = typeof props.mag === "number" ? props.mag : null;
  const place = typeof props.place === "string" ? props.place : null;
  const id = feature.id ?? `${lon},${lat},${props.time ?? snapshotMs}`;

  const t = isoFromMs(props.time, snapshotMs);
  // An earthquake is a point-in-time event; give the alert a generous 1h validity window so
  // the freshest hour of quakes stays live on the map.
  const stale = isoFromMs((typeof props.time === "number" ? props.time : snapshotMs) + 3_600_000, snapshotMs);
  const name =
    (typeof props.title === "string" && props.title) ||
    (mag != null ? `M ${mag.toFixed(1)}${place ? ` - ${place}` : ""}` : place || "Earthquake");

  return {
    id: `scop:usgs:${id}`,
    schema: "scop.envelope/1.0",
    kind: USGS_KIND,
    time: t,
    valid_time: { start: t, stale },
    geometry: { type: "Point", coordinates: [lon, lat] },
    properties: {
      name,
      affiliation: "neutral", // a natural event — never hostile, never friendly
      type: "earthquake",
      source: "usgs:earthquakes",
      domain: "seismic",
      // The HEADLINE fields: magnitude + depth + place (filterable / inspectable).
      magnitude: mag,
      depth: depthKm, // km below the surface (positive down)
      place,
      url: typeof props.url === "string" ? props.url : null,
      // Confidence so the alert flows through the Living-Net grammar: larger quakes read as
      // higher-confidence reports (clamped to a sane band). Presentation-only.
      confidence: mag != null ? Math.max(0.4, Math.min(0.99, +(0.5 + mag / 12).toFixed(2))) : 0.6,
    },
    security: {
      classification: "UNCLASSIFIED",
      releasability: [],
      mission: [],
      caveats: [],
      originator_control: false,
      policy_ref: "policy://defense.local",
      payload_mode: "clear",
      leakage_profile: "clear",
    },
    provenance: {
      originator: "usgs-earthquake-hazards",
      adapter: "usgs-earthquakes",
      received_at: isoFromMs(snapshotMs, snapshotMs),
      chain: [{ adapter: "usgs-earthquakes", at: isoFromMs(snapshotMs, snapshotMs) }],
    },
    payload: { clear: { magnitude: mag, depth_km: depthKm, place } },
  };
}

/** Map a whole USGS response → the alert envelope set. DROPS un-mappable (position-less /
 *  malformed) features (fail-closed). Pure + deterministic. */
export function responseToEnvelopes(resp: UsgsResponse): CanonicalEnvelope[] {
  const features = resp.features ?? [];
  const snapshotMs = resp.metadata?.generated ?? Date.now();
  return features.filter(isMappable).map((f) => featureToEnvelope(f, snapshotMs));
}

/** The bundled snapshot as alert envelopes (the offline/test set). */
export function snapshotEnvelopes(): CanonicalEnvelope[] {
  return responseToEnvelopes(USGS_SNAPSHOT_RESPONSE);
}

/** Fetch the LIVE USGS earthquake feed. Resolves to the parsed response on a 200, or `null`
 *  on ANY failure (network error, non-200, malformed body) — the caller falls back to the
 *  snapshot. NEVER throws (offline-graceful). A `fetchImpl` override makes this testable
 *  without a live network. */
export async function fetchLiveQuakes(
  fetchImpl: typeof fetch = fetch,
  signal?: AbortSignal,
): Promise<UsgsResponse | null> {
  try {
    const res = await fetchImpl(USGS_URL, { signal, headers: { Accept: "application/geo+json" } });
    if (!res.ok) return null; // throttle / 5xx / 4xx ⇒ fall back, do not retry tightly
    const body = (await res.json()) as UsgsResponse;
    // Defensive: a 200 with a null/absent features array is a valid "no quakes" answer, but a
    // non-array features field is malformed ⇒ fail-safe to the snapshot.
    if (body.features != null && !Array.isArray(body.features)) return null;
    return body;
  } catch {
    return null; // unreachable / DNS / timeout / parse error — caught, never thrown into host
  }
}
